import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import ProductList from '../components/ProductList';
import { categories, findCategory, findStore, slugify } from '../data/catalog';
import styles from '../styles/DealLanding.module.css';
import { prefetchDealsList } from '../utils/requestCache';
import { buildAlertCtaUrl } from '../utils/alertCta';

function readHubSummary() {
    if (typeof document === 'undefined') return null;
    const script = document.getElementById('esganga-hub-summary');
    if (!script) return null;
    try {
        return JSON.parse(script.textContent || script.innerHTML).summary || null;
    } catch {
        return null;
    }
}

function getNavigationStore(candidate, slug) {
    if (!candidate) return null;
    const normalizedSlug = slugify(slug).replace(/-/g, '');
    const matches = slugify(candidate.slug || '').replace(/-/g, '') === normalizedSlug
        || slugify(candidate.name || '').replace(/-/g, '') === normalizedSlug;
    return matches ? candidate : null;
}

const categoryCopy = {
    tecnologia: 'gangas de moviles, gadgets, informatica, accesorios y productos conectados con descuento real.',
    hogar: 'ofertas para cocina, orden, decoracion, herramientas y mejoras practicas para casa.',
    belleza: 'descuentos en cosmetica, perfumes, cuidado personal y farmacia online.',
    moda: 'rebajas en zapatillas, ropa deportiva, outlet y marcas con alta rotacion.',
    viajes: 'precios bajos en escapadas, vuelos, hoteles y reservas con fechas interesantes.',
    supermercado: 'ofertas en alimentacion, bebidas, bodega y compras recurrentes.',
    mascotas: 'descuentos en alimentacion, accesorios y productos utiles para perros y gatos.',
    'segunda-mano': 'productos reacondicionados, segunda mano y oportunidades con precio ajustado.',
    eventos: 'entradas, planes, conciertos y experiencias con promociones puntuales.',
    'velas-y-bienestar': 'ofertas de velas, aromas, cuidado del ambiente y bienestar para el hogar.'
};

const DealLanding = ({ type }) => {
    const params = useParams();
    const location = useLocation();
    const slug = type === 'category' ? params.categorySlug : params.storeSlug;
    const navigationStore = type !== 'category' ? getNavigationStore(location.state?.store, slug) : null;
    const [remoteStore, setRemoteStore] = useState(navigationStore);
    const [activeStores, setActiveStores] = useState([]);
    const [storeLookupError, setStoreLookupError] = useState(false);
    const [storeLookupReady, setStoreLookupReady] = useState(type === 'category' || Boolean(navigationStore));
    const [pageSummary, setPageSummary] = useState(readHubSummary);
    const [faqs, setFaqs] = useState([]);
    const catalogItem = type === 'category' ? findCategory(slug) : null;

    useEffect(() => {
        let active = true;
        const controller = new AbortController();
        const alreadyKnown = getNavigationStore(location.state?.store, slug);
        setRemoteStore(alreadyKnown);
        // Si venimos de un clic en el directorio de tiendas ya tenemos la tienda
        // exacta por navigation state: no bloqueamos el resto de la pagina
        // esperando este fetch (evita apilar otra ronda de cola/reintento del
        // limitador de lecturas publicas encima de las de ProductList).
        setStoreLookupReady(type === 'category' || Boolean(alreadyKnown));
        setStoreLookupError(false);
        fetch('/api/stores', { signal: controller.signal })
            .then((response) => response.ok ? response.json() : Promise.reject(new Error('Stores API unavailable')))
            .then((data) => {
                if (!active) {
                    return;
                }
                const normalizedSlug = slugify(slug).replace(/-/g, '');
                const stores = Array.isArray(data) ? data : [];
                setActiveStores(stores);
                if (type === 'category') return;
                const match = stores.find((store) => (
                    slugify(store.slug).replace(/-/g, '') === normalizedSlug
                    || slugify(store.name).replace(/-/g, '') === normalizedSlug
                ));
                // Si ya teniamos la tienda por navigation state, un refresco en
                // segundo plano que no la encuentre (lectura inconsistente,
                // catalogo justo reconciliandose, etc.) NO debe borrarla -- eso
                // convertia una tienda real, recien vista en el directorio, en
                // un "no existe" que mandaba al usuario a Navigate("/").
                setRemoteStore(match || alreadyKnown || null);
                setStoreLookupReady(true);
            })
            .catch((error) => {
                if (active && error.name !== 'AbortError' && !alreadyKnown) {
                    setStoreLookupError(true);
                    setStoreLookupReady(true);
                }
            });

        return () => {
            active = false;
            controller.abort();
        };
    }, [slug, type, location.state]);

    const item = catalogItem || remoteStore;

    useEffect(() => {
        // Snapshot SEO del servidor (buildPageSeoSnapshot en server/lib/seo.js)
        // aplicado tras una navegacion SPA: lo que ve el usuario (y Google en
        // su render JS) debe ser EXACTAMENTE el title/description que el HTML
        // inicial ya sirvio -- sin recarga no se regenera el <head> solo, y
        // una copia local se habia desviado del servidor (bug real 2026-09-03).
        // La description del servidor puede llevar cifras reales de hubStats
        // ("856 ofertas de tecnologia desde 19,99 EUR...") que el cliente no
        // puede calcular por su cuenta. No hay fallback local: pisar el <head>
        // con un titulo distinto creaba una divergencia visible para Google.
        let active = true;
        const pageNumber = Number(params.page) > 1 ? Number(params.page) : null;
        const pathname = type === 'category'
            ? `/chollos/${slug}${pageNumber ? `/pagina/${pageNumber}` : ''}`
            : `/tiendas/${slug}${pageNumber ? `/pagina/${pageNumber}` : ''}`;

        fetch(`/api/page-seo?path=${encodeURIComponent(pathname)}`)
            .then((response) => (response.ok ? response.json() : Promise.reject(new Error('page-seo unavailable'))))
            .then((data) => {
                if (!active || !data?.seo) {
                    return;
                }
                document.title = data.seo.title;
                const metaDescription = document.querySelector('meta[name="description"]');
                if (data.seo.description && metaDescription) {
                    metaDescription.setAttribute('content', data.seo.description);
                }
                if (data.summary) {
                    setPageSummary(data.summary);
                }
                // Mismas FAQs que el servidor ya prerenderizo sin JS
                // (buildCategoryListingFaqs en server/lib/seo.js) -- nunca un
                // segundo texto inventado en el cliente (mismo criterio que el
                // resto de esta pagina, ver comentario de mas arriba).
                setFaqs(Array.isArray(data.editorial?.faqs) ? data.editorial.faqs : []);
            })
            .catch(() => {});

        return () => {
            active = false;
        };
    }, [slug, type, params.page]);

    if (!item && !storeLookupReady) {
        return <main className={styles.page}><section className={styles.hero}><p>Cargando tienda...</p></section></main>;
    }

    if (!item && storeLookupError) {
        return <main className={styles.page}><section className={styles.hero}><h1>No hemos podido cargar la tienda</h1><p>Inténtalo de nuevo en unos instantes.</p></section></main>;
    }

    if (!item) {
        return <Navigate to="/" replace />;
    }

    const isCategory = type === 'category';
    const pageTitle = isCategory ? `Gangas de ${item.name}` : `Ofertas de ${item.name}`;
    const intro = isCategory
        ? `Seleccion de ${categoryCopy[item.slug] || `ofertas de ${item.name.toLowerCase()} con potencial de ahorro.`}`
        : `Descuentos y promociones activas de ${item.name}, reunidas para revisar rapido precio y detalles.`;
    const listDescription = isCategory && item.slug === 'viajes'
        ? 'Filtra rapido por vuelos, hoteles, escapadas o coche, y afina despues por precio, descuento y destino cuando haya datos.'
        : 'Usa los filtros para quedarte con las ofertas que encajan por precio, descuento o tienda.';

    return (
        <main className={styles.page}>
            <section className={styles.hero}>
                <p className={styles.kicker}>{isCategory ? 'Categoria' : 'Tienda'}</p>
                <h1>{pageTitle}</h1>
                {pageSummary ? <p>{pageSummary}</p> : <p>{intro}</p>}
                <div className={styles.heroStats} aria-label="Datos utiles de esta pagina">
                    <span><strong>50</strong> ofertas por pagina</span>
                    <span><strong>Nuevo</strong> recientes primero</span>
                    <span><strong>Filtros</strong> precio y descuento</span>
                </div>
                <div className={styles.links}>
                    <a href="#chollos">Ver ofertas</a>
                    {isCategory && <Link to={`/mejores/${item.slug}`}>Mejores {item.name.toLowerCase()} 2026</Link>}
                    <Link to={isCategory ? '/tiendas/amazon' : '/chollos/tecnologia'}>
                        {isCategory ? 'Explorar tiendas' : 'Explorar categorias'}
                    </Link>
                    {!isCategory && <Link to={`/cupones/${slug}`}>Ver cupones de {item.name}</Link>}
                    {!isCategory && (
                        <Link className={styles.alertLink} to={buildAlertCtaUrl({ keyword: item.name, category: item.category || findStore(slug)?.category, source: 'store' })}>
                            🔔 Avísame cuando haya ofertas
                        </Link>
                    )}
                </div>
            </section>

            <ProductList
                initialCategory={isCategory ? item.slug : 'Todos'}
                initialStore={isCategory ? '' : item.name}
                initialStoreSlug={isCategory ? '' : slug}
                eyebrow={isCategory ? 'Ofertas por categoria' : 'Ofertas por tienda'}
                title={isCategory ? `Descuentos activos en ${item.name}` : `Gangas activas de ${item.name}`}
                description={listDescription}
                initialSort="recent"
                showFilters
            />

            <section className={styles.related} aria-label="Enlaces relacionados">
                <div>
                    <h2>Categorias principales</h2>
                    <div className={styles.linkGrid}>
                        {categories.slice(0, 8).map((category) => (
                            <Link
                                key={category.slug}
                                to={`/chollos/${category.slug}`}
                                onMouseEnter={() => prefetchDealsList({ category: category.slug })}
                                onFocus={() => prefetchDealsList({ category: category.slug })}
                            >{category.name}</Link>
                        ))}
                    </div>
                </div>
                <div>
                    <h2>Tiendas destacadas</h2>
                    <div className={styles.linkGrid}>
                        {activeStores.slice(0, 8).map((store) => (
                            <Link
                                key={store.slug}
                                to={`/tiendas/${store.slug}`}
                                state={{ store }}
                                onMouseEnter={() => prefetchDealsList({ store: store.slug })}
                                onFocus={() => prefetchDealsList({ store: store.slug })}
                            >{store.name}</Link>
                        ))}
                    </div>
                </div>
            </section>

            {faqs.length > 0 && (
                <section className={styles.faqs} aria-label="Preguntas frecuentes">
                    <h2>Preguntas frecuentes</h2>
                    <dl>
                        {faqs.map((faq) => (
                            <React.Fragment key={faq.question}>
                                <dt>{faq.question}</dt>
                                <dd>{faq.answer}</dd>
                            </React.Fragment>
                        ))}
                    </dl>
                </section>
            )}
        </main>
    );
};

export default DealLanding;
